// 世界模拟器存储服务 - 管理世界定义、会话、心理档案等
import enhancedStorageService from './enhancedStorageService.js'
import { v4 as uuidv4 } from 'uuid'

class WorldSimulatorStorageService {
  constructor() {
    this.db = null
  }

  /**
   * 确保数据库已初始化
   */
  async ensureDB() {
    if (!this.db) {
      this.db = await enhancedStorageService.ensureDB()
    }
    
    // 检查必需的对象存储是否存在
    const requiredStores = ['worldSimulators', 'worldSessions', 'psychProfiles']
    const missingStores = requiredStores.filter(storeName => !this.db.objectStoreNames.contains(storeName))
    
    if (missingStores.length > 0) {
      console.warn('⚠️ 数据库缺少必需的对象存储:', missingStores, '，需要升级...')
      // 关闭当前数据库连接
      if (this.db) {
        this.db.close()
        this.db = null
      }
      // 清除 enhancedStorageService 的数据库连接，强制重新初始化
      enhancedStorageService.db = null
      // 增加数据库版本号以触发升级
      enhancedStorageService.dbVersion = enhancedStorageService.dbVersion + 1
      enhancedStorageService.initPromise = enhancedStorageService.initDB()
      
      // 重新初始化
      this.db = await enhancedStorageService.ensureDB()
      
      // 再次检查
      const stillMissing = requiredStores.filter(storeName => !this.db.objectStoreNames.contains(storeName))
      if (stillMissing.length > 0) {
        throw new Error(`数据库升级失败，缺少对象存储: ${stillMissing.join(', ')}。请手动刷新页面（Ctrl+F5 或 Cmd+Shift+R）`)
      }
    }
    
    return this.db
  }

  // ==================== 世界模拟器管理 ====================

  /**
   * 保存世界模拟器
   */
  async saveWorld(world) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['worldSimulators'], 'readwrite')
      const store = transaction.objectStore('worldSimulators')

      const worldData = {
        worldId: world.worldId || uuidv4(),
        name: world.name,
        description: world.description || '',
        summary: world.summary || '',
        coverImage: world.coverImage || '',
        creator: world.creator || { userId: 'guest', name: 'Guest', avatar: '' },
        type: world.type || 'mixed',
        style: world.style || [],
        tags: world.tags || [],
        playCount: world.playCount || 0,
        rating: world.rating || 0,
        ratingCount: world.ratingCount || 0,
        favoritesCount: world.favoritesCount || 0,
        estimatedDuration: world.estimatedDuration || '30-60分钟',
        difficulty: world.difficulty || 'medium',
        worldDefinition: world.worldDefinition || {},
        quantitativeSystems: world.quantitativeSystems || [],
        
        // 模板核心字段
        preText: world.preText || '',
        postText: world.postText || '', // AI核心指令集（完整保留，包括NSFW）
        prePrompt: world.prePrompt || '',
        worldBook: world.worldBook || [], // 知识库
        systems: world.systems || {}, // 系统设定
        rules: world.rules || [], // 规则
        language: world.language || 'zh-Hans',
        
        status: world.status || 'draft',
        isPublic: world.isPublic || false,
        isFeatured: world.isFeatured || false,
        createdAt: world.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        publishedAt: world.publishedAt || null,
        version: world.version || '1.0'
      }

      return new Promise((resolve, reject) => {
        const request = store.put(worldData)
        request.onsuccess = () => {
          console.log('✅ 世界模拟器已保存:', worldData.worldId)
          resolve(worldData)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 保存世界模拟器失败:', error)
      throw error
    }
  }

  /**
   * 更新世界模拟器
   */
  async updateWorld(worldId, updates) {
    try {
      const world = await this.getWorld(worldId)
      if (!world) {
        throw new Error('世界不存在')
      }

      const updatedWorld = {
        ...world,
        ...updates,
        updatedAt: new Date().toISOString()
      }

      return await this.saveWorld(updatedWorld)
    } catch (error) {
      console.error('❌ 更新世界模拟器失败:', error)
      throw error
    }
  }

  /**
   * 获取世界模拟器
   */
  async getWorld(worldId) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['worldSimulators'], 'readonly')
      const store = transaction.objectStore('worldSimulators')

      return new Promise((resolve, reject) => {
        const request = store.get(worldId)
        request.onsuccess = () => {
          resolve(request.result || null)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 获取世界模拟器失败:', error)
      return null
    }
  }

  /**
   * 获取所有世界模拟器
   */
  async getAllWorlds(creatorId = null) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['worldSimulators'], 'readonly')
      const store = transaction.objectStore('worldSimulators')

      return new Promise((resolve, reject) => {
        const request = creatorId
          ? store.index('creatorId').getAll(creatorId)
          : store.getAll()
        
        request.onsuccess = () => {
          const worlds = (request.result || []).sort((a, b) => {
            // 按创建时间倒序
            return new Date(b.createdAt) - new Date(a.createdAt)
          })
          resolve(worlds)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 获取所有世界模拟器失败:', error)
      return []
    }
  }

  /**
   * 获取公开的世界模拟器
   */
  async getPublicWorlds() {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['worldSimulators'], 'readonly')
      const store = transaction.objectStore('worldSimulators')
      const index = store.index('isPublic')

      return new Promise((resolve, reject) => {
        const request = index.getAll(true)
        request.onsuccess = () => {
          const worlds = (request.result || []).sort((a, b) => {
            // 热门优先：按播放量排序
            return b.playCount - a.playCount
          })
          resolve(worlds)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 获取公开世界模拟器失败:', error)
      return []
    }
  }

  /**
   * 删除世界模拟器
   */
  async deleteWorld(worldId) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['worldSimulators'], 'readwrite')
      const store = transaction.objectStore('worldSimulators')

      return new Promise((resolve, reject) => {
        const request = store.delete(worldId)
        request.onsuccess = () => {
          console.log('✅ 世界模拟器已删除:', worldId)
          resolve()
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 删除世界模拟器失败:', error)
      throw error
    }
  }

  /**
   * 增加播放量
   */
  async incrementPlayCount(worldId) {
    try {
      const world = await this.getWorld(worldId)
      if (world) {
        world.playCount = (world.playCount || 0) + 1
        await this.saveWorld(world)
      }
    } catch (error) {
      console.error('❌ 增加播放量失败:', error)
    }
  }

  // ==================== 世界会话管理 ====================

  /**
   * 创建会话
   */
  async createSession(worldId, userId = 'guest') {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['worldSessions'], 'readwrite')
      const store = transaction.objectStore('worldSessions')

      const session = {
        sessionId: uuidv4(),
        worldId,
        userId,
        currentParagraph: '',
        currentNodeId: null,
        backgroundImage: '',
        quantitativeValues: {},
        conversationHistory: [],
        choiceHistory: [],
        userMarkers: [],
        startTime: Date.now(),
        lastActiveTime: Date.now(),
        totalDuration: 0,
        status: 'active'
      }

      return new Promise((resolve, reject) => {
        const request = store.put(session)
        request.onsuccess = () => {
          console.log('✅ 会话已创建:', session.sessionId)
          resolve(session)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 创建会话失败:', error)
      throw error
    }
  }

  /**
   * 获取会话
   */
  async getSession(sessionId) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['worldSessions'], 'readonly')
      const store = transaction.objectStore('worldSessions')

      return new Promise((resolve, reject) => {
        const request = store.get(sessionId)
        request.onsuccess = () => {
          resolve(request.result || null)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 获取会话失败:', error)
      return null
    }
  }

  /**
   * 更新会话
   */
  async updateSession(sessionId, updates) {
    try {
      const session = await this.getSession(sessionId)
      if (!session) {
        throw new Error('会话不存在')
      }

      await this.ensureDB()
      const transaction = this.db.transaction(['worldSessions'], 'readwrite')
      const store = transaction.objectStore('worldSessions')

      const updatedSession = {
        ...session,
        ...updates,
        lastActiveTime: Date.now()
      }

      return new Promise((resolve, reject) => {
        const request = store.put(updatedSession)
        request.onsuccess = () => {
          resolve(updatedSession)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 更新会话失败:', error)
      throw error
    }
  }

  /**
   * 获取用户的所有会话
   */
  async getUserSessions(userId, worldId = null) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['worldSessions'], 'readonly')
      const store = transaction.objectStore('worldSessions')
      const index = store.index('userId')

      return new Promise((resolve, reject) => {
        const request = index.getAll(userId)
        request.onsuccess = () => {
          let sessions = request.result || []
          
          // 如果指定了worldId，过滤
          if (worldId) {
            sessions = sessions.filter(s => s.worldId === worldId)
          }
          
          // 按最后活跃时间排序
          sessions.sort((a, b) => b.lastActiveTime - a.lastActiveTime)
          
          resolve(sessions)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 获取用户会话失败:', error)
      return []
    }
  }

  /**
   * 删除会话
   */
  async deleteSession(sessionId) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['worldSessions'], 'readwrite')
      const store = transaction.objectStore('worldSessions')

      return new Promise((resolve, reject) => {
        const request = store.delete(sessionId)
        request.onsuccess = () => {
          console.log('✅ 会话已删除:', sessionId)
          resolve()
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 删除会话失败:', error)
      throw error
    }
  }

  // ==================== 心理档案管理 ====================

  /**
   * 保存心理档案
   */
  async savePsychProfile(userId, profile) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['psychProfiles'], 'readwrite')
      const store = transaction.objectStore('psychProfiles')

      const profileData = {
        userId,
        version: profile.version || '1.0',
        psychMirror: profile.psychMirror || {},
        tags: profile.tags || [],
        aiInsight: profile.aiInsight || '',
        conversationHistory: profile.conversationHistory || [],
        createdAt: profile.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      return new Promise((resolve, reject) => {
        const request = store.put(profileData)
        request.onsuccess = () => {
          console.log('✅ 心理档案已保存:', userId)
          resolve(profileData)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 保存心理档案失败:', error)
      throw error
    }
  }

  /**
   * 获取心理档案
   */
  async getPsychProfile(userId) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['psychProfiles'], 'readonly')
      const store = transaction.objectStore('psychProfiles')

      return new Promise((resolve, reject) => {
        const request = store.get(userId)
        request.onsuccess = () => {
          resolve(request.result || null)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 获取心理档案失败:', error)
      return null
    }
  }

  /**
   * 更新心理档案
   */
  async updatePsychProfile(userId, updates) {
    try {
      const profile = await this.getPsychProfile(userId)
      const updatedProfile = {
        ...profile,
        ...updates,
        updatedAt: new Date().toISOString()
      }
      return await this.savePsychProfile(userId, updatedProfile)
    } catch (error) {
      console.error('❌ 更新心理档案失败:', error)
      throw error
    }
  }
}

export default new WorldSimulatorStorageService()

